import React, { useState, useEffect } from "react";
import SEO from "../seo";
const Reviews = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "method-1-add-review-while-creating-product",
        "method-2-add-review-from-reviews-section",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Manage Product Reviews | xPage Help Center"
        description="Learn how to add and manage customer reviews in xPage. Add reviews while creating products or from the Reviews section, upload images, rate products, and manage review statuses easily."
        keywords="xPage, product reviews, manage reviews, add review, customer feedback, product rating, review images, publish review, eCommerce reviews"
        canonical="https://help.xpage.ai/help/business-expansion/Reviews"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Reviews
          </h1>
          <p className="text-sm text-systemLabel">
            With Xpage, you don't need a third-party app to manage customer
            reviews. Reviews are built directly into the system, making it easy
            to collect, link, and display them on your products.
          </p>
          <p className="text-sm text-systemLabel">
            You can create reviews in two ways:
          </p>
        </header>

        {/* Method 1: Add a Review While Creating a Product Section */}
        <section
          id="method-1-add-review-while-creating-product"
          className="mb-6 scroll-mt-20"
        >
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Method 1: Add a Review While Creating a Product
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4 mb-4">
              <li className="mb-2">
                <span className="font-medium">
                  When creating a product, look at the right sidebar and click
                  Add Review.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  You'll be redirected to the Reviews page, where a pop-up will
                  appear with your selected product already filled in.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Complete the review details:
                </span>
              </li>
            </ol>

            <div className="ml-6 mb-4">
              <h3 className="text-sm font-semibold text-systemTitle mb-2">
                Review Details:
              </h3>

              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Field
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      <strong className="text-systemTitle text-sm">
                        Description
                      </strong>
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Reviewer Info
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Name (example: "Smith Jalm")
                      <br />
                      Email (example: "smith@contact.com")
                    </td>
                  </tr>
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Write the Review
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Type feedback into the Review text area.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Upload Review Images
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Click Click to upload media to add up to 6 images.
                      <br />
                      <em>
                        Note: This demo dropzone may not store files
                        permanently.
                      </em>
                    </td>
                  </tr>
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Set Review Date
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Use the date picker to select when the review was written
                      (example: Aug 17, 2025).
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Rate the Product
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Select a rating using the star icons (example: 5 stars).
                    </td>
                  </tr>
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Choose Review Status
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Use the dropdown to set the status (e.g., Published with a
                      green dot showing it's active).
                    </td>
                  </tr>
                </tbody>
              </table>

              <h3 className="text-sm font-semibold text-systemTitle mb-2">
                Submit or Cancel:
              </h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
                <li>Click Create Review to save.</li>
                <li>Click Cancel to discard.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Method 2: Add a Review from the Reviews Section */}
        <section
          id="method-2-add-review-from-reviews-section"
          className="mb-6 scroll-mt-20"
        >
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Method 2: Add a Review from the Reviews Section
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4 mb-4">
              <li className="mb-2">
                <span className="font-medium">
                  In the left sidebar, go to Reviews.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click Add Review in the top right.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Fill out the form:</span>
              </li>
            </ol>

            <div className="ml-6 mb-4">
              <h3 className="text-sm font-semibold text-systemTitle mb-2">
                Review Form Details:
              </h3>

              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Field
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      <strong className="text-systemTitle text-sm">
                        Description
                      </strong>
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Select Product
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Use the dropdown labeled Product to choose the item being
                      reviewed.
                    </td>
                  </tr>
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Reviewer Info
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Name (example: "Smith Jalm")
                      <br />
                      Email (example: "smith@contact.com")
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Write the Review
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Type your feedback into the Review text area.
                    </td>
                  </tr>
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Upload Review Images
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Click Click to upload media to add up to 6 images.
                      <br />
                      <em>
                        Note: This demo dropzone may not store files
                        permanently.
                      </em>
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Set Review Date
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Use the date picker to select when the review was written
                      (example: Aug 17, 2025).
                    </td>
                  </tr>
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Rate the Product
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Select a rating using the star icons (example: 5 stars).
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Choose Review Status
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Use the dropdown to set the status (e.g., Published with a
                      green dot showing it's active).
                    </td>
                  </tr>
                </tbody>
              </table>

              <h3 className="text-sm font-semibold text-systemTitle mb-2">
                Submit or Cancel:
              </h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
                <li>Click Create Review to save.</li>
                <li>Click Cancel to discard.</li>
              </ul>
            </div>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">TIP:</span> Reviews created
                through either method are automatically linked to the selected
                product, so you can showcase authentic feedback without extra
                setup.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#method-1-add-review-while-creating-product"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "method-1-add-review-while-creating-product"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Method 1: Add Review While Creating Product
              </a>
            </li>
            <li>
              <a
                href="#method-2-add-review-from-reviews-section"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "method-2-add-review-from-reviews-section"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Method 2: Add Review from Reviews Section
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Reviews;
